import { buildHeaders } from "./apiConfig";

export const PROCESSING_STATUS = {
  UPLOADED: "UPLOADED", // files uploaded, processing queued / in progress
  PROCESSING: "PROCESSING", // actively being processed
  DOWNLOADED: "DOWNLOADED", // all translations done & ready to download
  COMPLETED: "COMPLETED", // fully complete
  FAILED: "FAILED", // error occurred
};

export const SYNC_STATUS = {
  VIDEO_TRANSLATED: "VIDEO_TRANSLATED",
};

// The Lambda may return job_id under different key names depending on
// the API version. This ensures job_id is always available on the object.
function normalizeJob(job) {
  const resolvedId =
    job.job_id || job.jobId || job.id || job.Job_id || job.JobId || job.JOB_ID || null;
  return { ...job, job_id: resolvedId };
}

// Real response: { success: true, user_email: "...", jobs: [...] }
export async function fetchUserJobs(userEmail, job_id, authToken = null) {
  if (!userEmail) throw new Error("User email is required to fetch jobs.");

  const payload = { user_email: userEmail };
  if (job_id?.length) payload.job_id = job_id;

  const url = process.env.REACT_APP_FETCH_AV_SYNC_STATUS_URL;

  if (!url) {
    throw new Error(
      "REACT_APP_FETCH_AV_SYNC_STATUS_URL is not defined. " +
        "Add it to your .env file or SSM Parameter Store."
    );
  }

  // eslint-disable-next-line no-console
  console.log(`[jobsService] Fetching jobs for: ${userEmail} and Job_id = ${job_id}`);

  const response = await fetch(url, {
    method: "POST",
    headers: buildHeaders(authToken),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errJson = await response.json().catch(() => ({}));
    const msg = (errJson.message || errJson.error || "").toLowerCase();
    // API returns 404/400 with "No jobs found" when user has no jobs yet — treat as empty
    if (response.status === 404 || msg.includes("no jobs found") || msg.includes("no jobs")) {
      return [];
    }
    throw new Error(errJson.message || errJson.error || `Server error: HTTP ${response.status}`);
  }

  const data = await response.json();
  // eslint-disable-next-line no-console
  console.log("[jobsService] Raw response:", data);

  if (data && typeof data === "object" && Array.isArray(data.jobs)) {
    if (data.success === false) throw new Error("API returned success=false. Please try again.");
    return data.jobs.map(normalizeJob);
  }

  // Fallback: direct array
  if (Array.isArray(data)) return data.map(normalizeJob);

  // Empty / unexpected → first-time user with no jobs
  // eslint-disable-next-line no-console
  console.warn("[jobsService] Unexpected response shape:", data);
  return [];
}

// Returns { done, total, label }  e.g. { done: 4, total: 6, label: "4/6" }
export function computeReadyCount(job) {
  const total = Array.isArray(job.languages) ? job.languages.length : 0;
  const done = Array.isArray(job.language_statuses)
    ? job.language_statuses.filter(
        (ls) => (ls.sync_status || "").toUpperCase() === SYNC_STATUS.VIDEO_TRANSLATED
      ).length
    : 0;
  return { done, total, label: total > 0 ? `${done}/${total}` : "—" };
}

export function deriveJobStats(jobs = []) {
  const stats = {
    total: jobs.length,
    completed: 0,
    processing: 0,
    ready: 0,
    failed: 0,
  };

  jobs.forEach((job) => {
    const ps = (job.processing_status || "").toUpperCase();
    const hasErr = job.error_status && job.error_status.trim() !== "";

    if (hasErr || ps === "FAILED") {
      stats.failed++;
      return;
    }
    if (ps === "DOWNLOADED" || ps === "COMPLETED") stats.completed++;
    if (ps === "UPLOADED" || ps === "PROCESSING") stats.processing++;

    // Ready = every language track is VIDEO_TRANSLATED
    const { done, total } = computeReadyCount(job);
    if (total > 0 && done === total) stats.ready++;
  });

  return stats;
}

// Only when processing_status is DOWNLOADED or COMPLETED
export function isDownloadEnabled(job) {
  const ps = (job?.processing_status || "").toUpperCase();
  return ps === "DOWNLOADED" || ps === "COMPLETED";
}

export function formatJobDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    const day = d.getDate();
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear();
    const time = d.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day} ${month} ${year}, ${time}`;
  } catch {
    return "—";
  }
}

export function formatServiceType(st = "") {
  if (!st) return "—";
  return st.charAt(0).toUpperCase() + st.slice(1).toLowerCase();
}
